import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Tv,
  Globe,
  Play,
  ArrowRight,
  Zap,
  BarChart3,
  Building,
  AudioWaveform,
  HeadphonesIcon,
  ChevronRight,
  Shield,
  Pause,
  Clock,
  Award,
} from "lucide-react";
import BackToTop from "../components/BackToTop/BackToTop";
import TestimonialCarousel, { testimonials } from "../components/TestimonialCarousel";
import Footer from "../components/Footer";
import Header from "../components/Header";
import HeroBg from "../../src/images/hero-bg.png";

export default function Home() {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const els = document.querySelectorAll(".reveal-text");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.3 }
    );

    els.forEach((el) => observer.observe(el));
  }, []);

  // Hide controls after 1 sec when playing
  useEffect(() => {
    let timeout;

    if (isPlaying) {
      timeout = setTimeout(() => {
        setShowControls(false);
      }, 1000);
    }

    return () => clearTimeout(timeout);
  }, [isPlaying]);

  const handleToggle = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setShowControls(true);
    } else {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setShowControls(true);
    }

    setIsPlaying(!isPlaying);
  };

  const handleMouseMove = () => {
    if (isPlaying) setShowControls(true);
  };

  const handleMouseLeave = () => {
    if (isPlaying) setShowControls(false);
  };


  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(progress);
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    const percentage = x / width;
    const newTime = percentage * videoRef.current.duration;

    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      setProgress(percentage * 100);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div>
      <BackToTop />

      {/* ================= HEADER ================= */}
      <Header />

      <div className="absolute inset-0 h-[350vh]">
        <img
          src={HeroBg}
          loading="lazy"
          sizes="(max-width: 1440px) 100vw, 1440px"
          alt="hero"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative">
        <main className="relative max-w-7xl mx-auto px-6  min-h-[calc(100vh-80px)] mt-20 flex items-center">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mt-10 lg:mt-0">

            {/* Left Column - Content */}
            <div className="text-center lg:text-left space-y-8">
              {/* Animated Badge */}
              <div className="flex justify-center lg:justify-start">
                <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-3 py-2 rounded-full border border-indigo-100 shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                  <span className="text-sm font-semibold text-gray-700 group-hover:text-indigo-600 transition-colors">
                    🚀 New: CTV Launchpad is here
                  </span>
                  <span className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs px-3 py-1 rounded-full font-medium shadow-md">
                    Live now
                  </span>
                </div>
              </div>

              {/* Main Heading with Gradient Animation */}
              <h1 className="text-5xl md:text-6xl font-bold leading-tight reveal-text">
                Transform Your Social Media Posts into
                <span className="block mt-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent animate-gradient">
                  Stunning TV Ads
                </span>
              </h1>

              <p className="text-gray-600 text-lg  max-w-2xl mx-auto lg:mx-0 leading-relaxed reveal-text">
                Unlock your social content from{" "}
                <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                  Instagram, Youtube, and Facebook
                </span>{" "}
                with our AI-powered platform that creates premium CTV experiences in minutes.
              </p>


              {/* CTA Buttons */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-4 pt-2">
                <Link
                  to="/dashboard/editor"
                  className="group relative flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3.5 rounded-full overflow-hidden shadow-lg hover:shadow-indigo-200/50 transition-all duration-300 hover:scale-105"
                >
                  <span className="relative z-10 flex items-center gap-2 text-base font-semibold">
                    Start Creating Free
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                </Link>
                <Link
                  to="/templates"
                  className="flex items-center gap-2 bg-white border-2 border-gray-200 text-gray-700 px-8 py-3.5 rounded-full hover:border-indigo-300 hover:bg-indigo-50/30 transition-all duration-300 shadow-sm hover:shadow-md font-semibold"
                >
                  <Play size={16} />
                  <span>Watch Demo</span>
                </Link>
              </div>
            </div>

            {/* Right Column - Modern Video Player */}
            <div className="relative group">
              <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-2 shadow-2xl">
                <div className="relative rounded-2xl overflow-hidden bg-black">
                  <video
                    src="https://test-pages.b-cdn.net/SocialTV/Master/layout3.mp4"
                    className="w-full h-full object-cover aspect-video"
                    playsInline
                    preload="auto"
                    loop
                    autoPlay
                    muted
                  />
                  <div className="absolute top-2 left-4 right-4 flex justify-between z-10">
                    <div></div>
                    <div className="flex gap-2">
                      <span className="px-2 py-1 bg-black/50 backdrop-blur-sm rounded-lg text-xs text-white">
                        ⚡ Live Demo
                      </span>
                    </div>
                  </div>

                </div>

                {/* TV Stand */}
                <div className="absolute -bottom-9 left-1/2 transform -translate-x-1/2 w-20 h-10 bg-gradient-to-b from-gray-800 to-gray-900 rounded-b-lg"></div>
                <div className="absolute -bottom-9 left-1/2 transform -translate-x-1/2 w-40 h-1.5 bg-gray-900 rounded-full"></div>
              </div>

              {/* Floating Stats Card */}
              <div className="absolute -bottom-6 -right-6   bg-[#fff6] bg-[linear-gradient(77deg,_#fff,_#fff3_42%)] rounded-2xl shadow-xl p-4 backdrop-blur-sm hidden lg:block animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-800">Processing Time</p>
                    <p className="text-lg font-bold text-gray-900">~30 seconds</p>
                  </div>
                </div>
              </div>

              {/* Stats Badge Bottom */}
              <div className="absolute -bottom-4 -left-4  bg-[#fff6] bg-[linear-gradient(77deg,_#fff,_#fff3_42%)]  backdrop-blur-sm rounded-full px-4 py-2 shadow-lg border border-gray-200 hidden lg:flex items-center gap-3 animate-float-delayed">
                <div className="flex items-center gap-1">
                  <span className="text-indigo-500"><AudioWaveform /></span>
                  <span className="text-sm font-semibold text-gray-900">99.9% Uptime</span>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Add custom animations */}
        <style jsx>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes floatDelayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s ease infinite;
        }
        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: floatDelayed 3.5s ease-in-out infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
      </div>


      <section className="relative py-16 sm:py-20 md:py-24 lg:py-28 overflow-hidden ">

        <div className="relative max-w-7xl mx-auto px-6">
          {/* Header Section with animated gradient */}
          <div className="text-center max-w-4xl mx-auto mb-20">
            {/* Animated Badge */}
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-gray-200/50 px-4 py-2 rounded-full mb-8 shadow-lg hover:shadow-xl transition-all duration-300 group">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
              </span>
              <span className="text-sm font-medium text-gray-700 group-hover:text-indigo-600 transition-colors">
                ✨ Interactive Demo
              </span>
              <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
            </div>

            {/* Main Title with gradient animation */}
            <h2 className="reveal-text text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-gray-900 via-indigo-600 to-purple-600 bg-clip-text text-transparent animate-gradient">
              See the Magic Happen
            </h2>

            <p className="reveal-text text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Watch how your social media content transforms into a stunning
              <span className="font-semibold text-indigo-600"> Connected TV experience</span> in real-time.
            </p>
          </div>

          {/* Feature Tags - Floating Animation */}
          <div className="flex flex-wrap justify-center gap-3 mb-12 hidden">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="group relative cursor-default"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} rounded-full blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-300`}></div>
                <div className="relative flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
                  <feature.icon className={`w-4 h-4 text-${feature.color.split(' ')[1]}`} />
                  <span className="text-sm font-medium text-gray-700">{feature.title}</span>
                  <span className="text-xs text-gray-400 hidden sm:inline">•</span>
                  <span className="text-xs text-gray-500 hidden sm:inline">{feature.description}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Main Video Card */}
          <div className="max-w-5xl mx-auto">
            <div className="relative group">
              {/* Animated gradient border */}
              <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-3xl blur-xl opacity-25 group-hover:opacity-50 transition duration-500"></div>

              {/* Main Card */}
              <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden">
                {/* Modern Card Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Window controls */}
                      <div className="flex gap-2">
                        <div className="w-3 h-3 bg-red-400 rounded-full hover:bg-red-500 transition-colors cursor-pointer"></div>
                        <div className="w-3 h-3 bg-yellow-400 rounded-full hover:bg-yellow-500 transition-colors cursor-pointer"></div>
                        <div className="w-3 h-3 bg-green-400 rounded-full hover:bg-green-500 transition-colors cursor-pointer"></div>
                      </div>
                      <div className="h-4 w-px bg-gray-200"></div>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-gray-700">SocialTV Studio</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-full font-medium">4K • HDR</span>
                    </div>
                  </div>
                </div>

                {/* Video Container */}
                <div
                  className="relative aspect-video bg-gradient-to-br from-gray-900 to-black"
                  onMouseEnter={() => setShowControls(true)}
                  onMouseLeave={() => setShowControls(false)}
                >
                  {/* Video Element */}
                  <video
                    ref={videoRef}
                    src="https://test-pages.b-cdn.net/SocialTV/Master/layout5.mp4"
                    className="w-full h-full object-cover"
                    playsInline
                    preload="metadata"
                    onClick={handleToggle}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={(e) => {
                      if (e.target) {
                        setDuration(e.target.duration);
                      }
                    }}
                    onEnded={() => {
                      setIsPlaying(false);
                      setShowControls(true);
                    }}
                  />

                  {/* Play/Pause Overlay Button */}
                  {(showControls || !isPlaying) && (
                    <button
                      onClick={handleToggle}
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-300 group z-10"
                    >
                      {isPlaying ? (
                        <Pause className="w-6 h-6 text-gray-800 group-hover:text-indigo-600" />
                      ) : (
                        <Play className="w-6 h-6 ml-1 text-gray-800 group-hover:text-indigo-600" />
                      )}
                    </button>
                  )}

                  {/* Video Controls Bar */}
                  {showControls && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-8 pb-3 px-4 z-10 animate-slide-up">
                      {/* Time Display */}
                      <div className="flex justify-between text-xs text-white/80 mb-1">
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(duration)}</span>
                      </div>

                      {/* Progress Bar */}
                      <div
                        className="h-1 bg-white/30 rounded-full cursor-pointer group/progress"
                        onClick={handleSeek}
                      >
                        <div
                          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full relative"
                          style={{ width: `${progress}%` }}
                        >
                          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg opacity-0 group-hover/progress:opacity-100 transition-opacity"></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Card Footer with Social Integration */}
                <div className="px-6 py-5 bg-white">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      {/* Platform Avatars */}
                      <div className="flex -space-x-2">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 border-2 border-white shadow-md flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.919-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                          </svg>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 border-2 border-white shadow-md flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                          </svg>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-900 to-gray-700 border-2 border-white shadow-md flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                          </svg>
                        </div>
                      </div>

                      {/* Description */}
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Instagram → Youtube → Facebook → CTV</p>
                        <p className="text-xs text-gray-500">Seamless transformation in under 30 seconds</p>
                      </div>
                    </div>

                    {/* CTA Button */}
                    <Link
                      to="/dashboard/editor"
                      className="group relative inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full font-medium shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 overflow-hidden"
                    >
                      <span className="relative z-10">Try Now</span>
                      <ArrowRight className="relative z-10 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      <div className="absolute inset-0 bg-gradient-to-r from-indigo-700 to-purple-700 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Indicator */}
          <div className="text-center mt-12">
            <p className="text-sm text-gray-400 flex items-center justify-center gap-2">
              <Shield className="w-4 h-4" />
              Trusted by 10,000+ creators worldwide
            </p>
          </div>
        </div>

        {/* Add animation keyframes to your global CSS */}
        <style jsx>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s ease infinite;
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
      </section>

      {/* ================= WHY SECTION ================= */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-28  bg-white relative overflow-hidden border-t border-b border-gray-100">
        {/* Animated Background Elements - More subtle and modern */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 -left-40 w-[500px] h-[500px] bg-gradient-to-r from-indigo-200/20 via-purple-200/20 to-pink-200/20 rounded-full blur-3xl animate-float-slow"></div>
          <div className="absolute bottom-0 -right-40 w-[600px] h-[600px] bg-gradient-to-l from-blue-200/20 via-cyan-200/20 to-teal-200/20 rounded-full blur-3xl animate-float-slower"></div>


          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-full animate-float-particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 8}s`,
                animationDuration: `${15 + Math.random() * 15}s`,
                opacity: 0.2,
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-6 relative">
          {/* Section Header - Modern & Clean */}
          <div className="text-center max-w-3xl mx-auto mb-20">
            {/* Modern Animated Badge */}
            <div className="inline-flex items-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-full mb-8 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all duration-300 group cursor-default">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
              </span>
              <span className="text-sm font-medium text-gray-700 group-hover:text-indigo-600 transition-colors">
                Why Choose Us
              </span>
              {/* <span className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full">New</span> */}
            </div>

            {/* Main Title - Clean & Bold */}
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              <span className="text-gray-900">Why</span>
              <span className="relative inline-block mx-3">
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Social CTV
                </span>
                <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-full"></span>
              </span>
            </h2>

            <span className="reveal-text block text-2xl md:text-3xl text-gray-600 font-normal mt-4">
              The smarter way to advertise on connected TV
            </span>

            {/* Subtitle */}
            <p className="reveal-text text-xl text-gray-500 mt-2 mb-8 leading-relaxed">
              Transform your social media presence into premium TV experiences with our innovative platform
            </p>


            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center gap-6">
              {[
                { label: 'Enterprise-grade', icon: Building }, // building
                { label: '99.9% Uptime', icon: AudioWaveform }, // audio-waveform
                { label: '24/7 Support', icon: HeadphonesIcon }, // headphones
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-gray-500">
                  <span><item.icon /></span>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Main Cards Grid - Modern Design */}
          <div className="grid md:grid-cols-3 gap-8 lg:gap-6 xl:gap-8 mb-24">
            {/* Card 1 - Reach Big Screens */}
            <div className="group relative bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 h-full w-full">
                <img
                  src={HeroBg}
                  loading="lazy"
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Card content */}
              <div className="relative p-8">
                {/* Icon with animated background */}
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <Tv className="h-8 w-8" />
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  Reach Big Screens
                </h3>

                {/* Description */}
                <p className="text-gray-500 mb-6 leading-relaxed">
                  Transform your social creatives into stunning TV ads that captivate audiences on the biggest screens.
                </p>

                {/* Stats badge */}
                <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-600 px-3 py-1.5 rounded-full text-sm font-medium mb-6">
                  <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                  2.5B+ Connected TVs
                </div>

                {/* Features list */}
                <ul className="space-y-3">
                  {[
                    '4K Ultra HD Quality',
                    'HDR10+ Support',
                    'Multi-Device Sync',
                    'Smart TV Optimized'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Learn more link */}
                <div className="mt-8 pt-4 border-t border-indigo-100">
                  <Link href="#" className="text-blue-600 font-medium inline-flex items-center gap-2 group/link">
                    Learn more
                    <svg className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>

            {/* Card 2 - Fast Production */}
            <div className="reveal-text group relative bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden">
              {/* <div className="absolute inset-0 bg-gradient-to-br from-orange-600/5 via-red-600/5 to-pink-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div> */}

              <div className="absolute inset-0 h-full w-full">
                <img
                  src={HeroBg}
                  loading="lazy"
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative p-8">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 group-hover:-rotate-3 transition-all duration-300">
                    <Zap className="h-8 w-8" />
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">
                  Fast Production
                </h3>

                <p className="text-gray-500 mb-6 leading-relaxed">
                  Turn any social post into professional CTV ads in minutes, not days. AI handles the heavy lifting.
                </p>

                <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-600 px-3 py-1.5 rounded-full text-sm font-medium mb-6">
                  <span className="w-1.5 h-1.5 bg-orange-600 rounded-full"></span>
                  85% Faster Production
                </div>

                <ul className="space-y-3">
                  {[
                    'AI-Powered Editing',
                    'Auto-Formatting',
                    'Batch Processing',
                    'Template Library'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-orange-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-4 border-t border-orange-100">
                  <Link href="#" className="text-orange-600 font-medium inline-flex items-center gap-2 group/link">
                    Learn more
                    <svg className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>

            {/* Card 3 - Measure Performance */}
            <div className="reveal-text group relative bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden">
              {/* <div className="absolute inset-0 bg-gradient-to-br from-green-600/5 via-emerald-600/5 to-teal-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div> */}

              <div className="absolute inset-0 h-full w-full">
                <img
                  src={HeroBg}
                  loading="lazy"
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative p-8">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <BarChart3 className="h-8 w-8" />
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors">
                  Measure Performance
                </h3>

                <p className="text-gray-500 mb-6 leading-relaxed">
                  Track real-time views, engagement metrics, and ROI across all your CTV campaigns.
                </p>

                <div className="inline-flex items-center gap-2 bg-green-50 text-green-600 px-3 py-1.5 rounded-full text-sm font-medium mb-6">
                  <span className="w-1.5 h-1.5 bg-green-600 rounded-full animate-pulse"></span>
                  Real-time Analytics
                </div>

                <ul className="space-y-3">
                  {[
                    'Live Dashboard',
                    'Engagement Tracking',
                    'ROI Calculator',
                    'Audience Insights'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-4 border-t border-green-100">
                  <Link href="#" className="text-green-600 font-medium inline-flex items-center gap-2 group/link">
                    Learn more
                    <svg className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Stats Grid - Modern Design */}
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 reveal-text">
              <StatCard
                number="98%"
                label="Client Satisfaction"
                trend="12%"
                trendUp={true}
                color="blue"
              />
              <StatCard
                number="50M+"
                label="Ads Delivered"
                trend="25%"
                trendUp={true}
                color="orange"
              />
              <StatCard
                number="150+"
                label="Enterprise Clients"
                trend="30%"
                trendUp={true}
                color="green"
              />
              <StatCard
                number="24/7"
                label="Support Available"
                trend="Live"
                trendUp={true}
                color="purple"
              />
            </div>

            {/* Bottom CTA */}
            <div className="text-center mt-16">

              <Link
                to={`/dashboard/editor`}
                className="group relative inline-flex items-center gap-3 bg-indigo-600 bg-[length:200%_100%] text-white px-8 py-4 rounded-xl transition-all duration-500 shadow-lg hover:shadow-indigo-500/25  font-medium animate-shimmer"
              >
                <span>Start your free trial</span>
                <svg className="w-5 h-5 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </Link>
            </div>
          </div>
        </div>
      </section>


      {/* ================= HOW IT WORKS SECTION ================= */}
      {/* <section className="py-16 sm:py-20 md:py-24 lg:py-28  bg-white relative overflow-hidden" >
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_30%,rgba(99,102,241,0.1),transparent_50%)]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="inline-flex items-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-full mb-8 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all duration-300 group cursor-default">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
              </span>
              <span className="text-sm font-medium text-gray-700 group-hover:text-indigo-600 transition-colors">
                Simple Process
              </span>
            </div>

            <h2 className="reveal-text text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gray-900">How It</span>
              <span className="relative inline-block mx-3">
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Works
                </span>
                <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full"></span>
              </span>
            </h2>

            <p className="reveal-text text-xl text-gray-500 max-w-2xl mx-auto">
              Transform your social content into stunning CTV ads in just three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            <div className="reveal-text group relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 blur-lg"></div>
              <div className="relative bg-white p-8 rounded-3xl border border-indigo-100 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                    1
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Choose Template</h3>
                <p className="text-gray-500 mb-4">
                  Select from 10+ professional CTV ad templates. Each template is optimized for TV screens and VAST compatibility.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="px-3 py-1 bg-indigo-50 text-indigo-600 text-xs rounded-full">Modern</span>
                  <span className="px-3 py-1 bg-purple-50 text-purple-600 text-xs rounded-full">Corporate</span>
                  <span className="px-3 py-1 bg-indigo-50 text-indigo-600 text-xs rounded-full">Dynamic</span>
                </div>
              </div>
            </div>

            <div className="reveal-text group relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 blur-lg"></div>
              <div className="relative bg-white p-8 rounded-3xl border border-indigo-100 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                    2
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Add Your Content</h3>
                <p className="text-gray-500 mb-4">
                  Paste your video URL from YouTube, Facebook, or social media. Add your brand logo and messaging to customize.
                </p>
                <div className="flex items-center gap-2 mt-4 text-sm text-purple-600">
                  <div>
                    <Zap className="h-4 w-4" />
                  </div>
                  <span>Auto 4K • HDR • 60fps</span>
                </div>
              </div>
            </div>

            <div className="reveal-text group relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-500 to-indigo-500 rounded-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 blur-lg"></div>
              <div className="relative bg-white p-8 rounded-3xl border border-indigo-100 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                    3
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Get VAST Tag</h3>
                <p className="text-gray-500 mb-4">
                  Generate your VAST tag URL instantly. Copy and paste into any ad server or CTV platform.
                </p>
                <div className="flex items-center flex-wrap gap-2 mt-4">
                  <span className="px-3 py-1 bg-green-50 text-green-600 text-xs rounded-full">Ready in 30s</span>
                  <span className="px-3 py-1 bg-indigo-50 text-indigo-600 text-xs rounded-full">VAST 4.2</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section> */}

      <section className="py-16 sm:py-20 md:py-24 lg:py-28 bg-white relative overflow-hidden">

        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-r from-indigo-100/40 to-purple-100/40 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-r from-pink-100/40 to-indigo-100/40 rounded-full blur-3xl"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(99,102,241,0.03),transparent_70%)]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">

          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 bg-white border border-gray-200 px-5 py-2 rounded-full mb-6 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all duration-300 group cursor-default">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
              </span>
              <span className="text-sm font-medium text-gray-700 group-hover:text-indigo-600 transition-colors">
                Simple Process
              </span>
            </div>

            <h2 className="reveal-text text-4xl md:text-5xl  font-bold mb-5">
              <span className="text-gray-900">How It</span>
              <span className="relative inline-block mx-3">
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Works
                </span>
                <span className="absolute -bottom-3 left-0 w-full h-1 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-full"></span>
              </span>
            </h2>

            <p className="reveal-text text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed">
              Transform your social content into stunning CTV ads in just three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">

            <div className="reveal-text group relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl opacity-0 group-hover:opacity-25 transition-all duration-500 blur-xl"></div>

              <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100">
                <div className="h-1.5 bg-gradient-to-r from-indigo-500 to-purple-500"></div>

                <div className="p-8">
                  <div className="flex justify-between items-start mb-6">
                    <div className="relative">
                      <div className="relative w-14 h-14 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                        <span className="text-white text-2xl font-bold">1</span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></div>
                      <div className="w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
                      <div className="w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors">
                    Choose Template
                  </h3>

                  <p className="text-gray-500 text-base leading-relaxed mb-5">
                    Select from 10+ professional CTV ad templates. Each template is optimized for TV screens and VAST compatibility.
                  </p>

                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1.5 bg-indigo-50 text-indigo-600 text-xs font-medium rounded-lg">Modern</span>
                    <span className="px-3 py-1.5 bg-purple-50 text-purple-600 text-xs font-medium rounded-lg">Corporate</span>
                    <span className="px-3 py-1.5 bg-indigo-50 text-indigo-600 text-xs font-medium rounded-lg">Dynamic</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="reveal-text group relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl opacity-0 group-hover:opacity-25 transition-all duration-500 blur-xl"></div>

              <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100">
                <div className="h-1.5 bg-gradient-to-r from-purple-500 to-pink-500"></div>

                <div className="p-8">
                  <div className="flex justify-between items-start mb-6">
                    <div className="relative">
                      <div className="relative w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                        <span className="text-white text-2xl font-bold">2</span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <div className="w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
                      <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
                      <div className="w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors">
                    Add Your Content
                  </h3>

                  <p className="text-gray-500 text-base leading-relaxed mb-5">
                    Paste your video URL from YouTube, Facebook, or social media. Add your brand logo and messaging to customize.
                  </p>

                  <div className="flex items-center gap-2 text-sm">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 rounded-lg">
                      <Zap className="h-3.5 w-3.5 text-purple-500" />
                      <span className="text-purple-600 text-xs font-medium">Auto 4K</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 rounded-lg">
                      <span className="text-purple-600 text-xs font-medium">HDR</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 rounded-lg">
                      <span className="text-purple-600 text-xs font-medium">60fps</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="reveal-text group relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-500 to-indigo-500 rounded-2xl opacity-0 group-hover:opacity-25 transition-all duration-500 blur-xl"></div>

              <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100">
                <div className="h-1.5 bg-gradient-to-r from-pink-500 to-indigo-500"></div>

                <div className="p-8">
                  <div className="flex justify-between items-start mb-6">
                    <div className="relative">
                      <div className="relative w-14 h-14 bg-gradient-to-r from-pink-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
                        <span className="text-white text-2xl font-bold">3</span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <div className="w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
                      <div className="w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
                      <div className="w-1.5 h-1.5 bg-pink-500 rounded-full"></div>
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-pink-600 transition-colors">
                    Get VAST Tag
                  </h3>

                  <p className="text-gray-500 text-base leading-relaxed mb-5">
                    Generate your VAST tag URL instantly. Copy and paste into any ad server or CTV platform.
                  </p>

                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1.5 bg-green-50 text-green-600 text-xs font-medium rounded-lg flex items-center gap-1">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                      Ready in 30s
                    </span>
                    <span className="px-3 py-1.5 bg-indigo-50 text-indigo-600 text-xs font-medium rounded-lg">VAST 4.2</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ================= TESTIMONIALS SECTION ================= */}
      < section className="py-16 sm:py-20 md:py-24 lg:py-28   border-t border-slate-100 bg-white relative overflow-hidden" >

        {/* Animated background elements */}
        <div div className="absolute inset-0" >
          <div className="absolute top-40 left-20 w-96 h-96 bg-indigo-200/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-40 right-20 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl animate-pulse animation-delay-2000"></div>
        </div >

        <div className="absolute inset-0 overflow-hidden">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${15 + Math.random() * 15}s`,
                opacity: 0.2,
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10 md:mb-12 lg:mb-16">
            <div className="inline-flex items-center gap-2 bg-white border border-gray-200 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full mb-4 sm:mb-6 md:mb-8 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all duration-300 group cursor-default">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
              </span>
              <span className="text-xs sm:text-sm font-medium text-gray-700 group-hover:text-indigo-600 transition-colors">
                Testimonials
              </span>
            </div>

            <h2 className="reveal-text text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-2 sm:mb-3 md:mb-4 px-2">
              <span className="text-gray-900">What Our</span>
              <span className="relative inline-block mx-1 sm:mx-2 md:mx-3">
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Creators
                </span>
                <span className="absolute -bottom-1 sm:-bottom-2 left-0 w-full h-0.5 sm:h-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full"></span>
              </span>
              <span className="text-gray-900">Say</span>
            </h2>

            <p className="reveal-text text-sm sm:text-base md:text-lg lg:text-xl text-gray-500 max-w-2xl mx-auto px-4">
              Join thousands of marketers and creators who transform their social content into stunning CTV ads
            </p>
          </div>

          {/* Testimonial Carousel */}
          <TestimonialCarousel testimonials={testimonials} />
        </div>
      </section>


      {/* ================= TRUSTED SECTION ================= */}
      <section className="py-16 sm:py-20 bg-white border-t border-b border-slate-100 hidden" >
        <div className="max-w-6xl mx-auto px-6 text-center">

          <div className="flex items-center justify-center gap-2 mb-4">
            <Globe className="h-5 w-5 text-black" />
            <p className="text-xs tracking-[4px] font-semibold text-gray-500">
              TRUSTED BY TOP BRANDS AROUND THE WORLD
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 mt-10">
            <BrandPill name="Nike" />
            <BrandPill name="Meta" />
            <BrandPill name="Netflix" />
            <BrandPill name="Amazon" />
            <BrandPill name="Shopify" />
            <BrandPill name="Zara" />
          </div>
        </div>
      </section>


      {/* ================= FAQ SECTION ================= */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-28  bg-white" >
        <div className="max-w-5xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-full mb-8 shadow-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
              </span>
              <span className="text-sm font-medium text-gray-700">Got Questions?</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gray-900">Frequently Asked</span>
              <span className="relative inline-block mx-3">
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Questions
                </span>
                <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full"></span>
              </span>
            </h2>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {[
              {
                q: "What is a VAST tag and why do I need it?",
                a: "VAST (Video Ad Serving Template) is a universal format that allows video ads to be served across different platforms and devices. Your VAST tag is like a URL that tells ad servers where to find your video ad and how to display it. With SocialTV, we generate this automatically for you."
              },
              {
                q: "Can I use content from any social media platform?",
                a: "Yes! SocialTV supports imports from Instagram, Youtube, Facebook, X (Twitter), Pinterest, YouTube, and Vimeo. Simply paste the URL and our AI will optimize it for TV display."
              },
              {
                q: "How long does it take to create a CTV ad?",
                a: "Most users can create a professional CTV ad in under 5 minutes. Simply choose a template, paste your social media URL, and we handle the rest. Advanced customizations may take a bit longer."
              },
              {
                q: "What platforms can I run my ads on?",
                a: "Your VAST tag works with all major CTV platforms including Roku, Amazon Fire TV, Apple TV, Samsung TV Plus, LG Channels, and any ad server that supports VAST 4.2 (Google Ad Manager, The Trade Desk, etc.)."
              },
              {
                q: "Do I need video editing experience?",
                a: "Not at all! Our AI-powered platform handles all the technical aspects. You just need your social media content and we'll transform it into a stunning TV ad automatically."
              },
              {
                q: "Is there a free trial?",
                a: "Yes! Our free plan lets you create up to 3 videos per month. No credit card required. When you're ready for more, you can upgrade to Pro for unlimited videos and 4K quality."
              }
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
                <details className="group">
                  <summary className="flex items-center justify-between cursor-pointer list-none">
                    <h3 className="text-lg font-semibold text-gray-900">{faq.q}</h3>
                    <span className="transition-transform duration-300 group-open:rotate-180">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  </summary>
                  <p className="mt-4 text-gray-600 leading-relaxed">{faq.a}</p>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      < Footer />

    </div>
  );
}

function BrandPill({ name }) {
  return (
    <div className="px-6 py-3 bg-white border rounded-full shadow-sm hover:shadow-xl hover:-translate-y-1 hover:scale-105 transition-all duration-300 font-semibold text-gray-700 cursor-pointer relative overflow-hidden group">
      <span className="relative z-10">{name}</span>
      <div className="absolute inset-0 bg-gradient-to-r from-black/5 to-transparent transform -translate-x-full group-hover:translate-x-0 transition-transform"></div>
    </div>
  );
}

const StatCard = ({ number, label, trend, trendUp, color }) => {
  const colors = {
    blue: 'from-blue-500 to-indigo-500',
    orange: 'from-orange-500 to-red-500',
    green: 'from-green-500 to-emerald-500',
    purple: 'from-purple-500 to-pink-500'
  };

  const trendColor = trendUp
    ? "text-green-600 bg-green-100/60"
    : "text-red-600 bg-red-50";

  return (
    <div className="group bg-white rounded-2xl p-6 text-center border border-gray-200 hover:border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-default">
      <div className={`text-3xl font-bold bg-gradient-to-r ${colors[color]} bg-clip-text text-transparent mb-1`}>
        {number}
      </div>
      <div className="text-sm text-gray-500 mb-2">{label}</div>

      {/* Trend */}
      {trend && (
        <div
          className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${trendColor}`}
        >
          <span>{trend}</span>

          {trend !== "Live" && (
            <svg
              className={`w-3 h-3 ${trendUp ? "" : "rotate-180"}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>
      )}
    </div>
  );
};


const features = [
  { icon: Shield, title: "4K Quality", description: "Crystal clear output every time", color: "from-blue-500 to-cyan-500" },
  { icon: Clock, title: "Real-time", description: "Instant preview and rendering", color: "from-green-500 to-emerald-500" },
  { icon: Award, title: "Platform Ready", description: "Optimized for all CTV platforms", color: "from-orange-500 to-red-500" }
];